/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * Sagas for table remove row
 *
 * @author Anton Panasenko
 * @version 1.4.0
 * @since 1.3.0
 */

import { createElement } from 'react';

import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';

import { API_HOSTNAME } from '../../../modules/app/Api';
import { modalOpen } from '../../../modules/modal/modalActions';
import { IModalRenderProps } from '../../../modules/modal/types/modalTypes';
import { checkResponseErrorWithModal, requestSaga } from '../../../sagas/requestSaga';
import {
    setTableIsLoading,
    tableRemoveRowFailure,
    tableRemoveRowSuccess,
} from '../actions/tableActions';
import TableRemoveRowModalPopup from '../components/TableColumns/TableRemoveRow/TableRemoveRowModalPopup';
import {
    makeSelectorTableCurrentName,
} from '../selectors/tableSelectors';
import {
    MapTableNameToRemoveApi,
    MapTableNameToRemoveCallback,
    MapTableNameToRemoveRowTranslationSelector,
} from '../types/tableMapTypes';
import { TableNameType, TTableModalOpenRemoveAction, TTableRemoveRowAction } from '../types/tableTypes';

export function* tableRemoveRowSaga(action: TTableRemoveRowAction): SagaIterator {
    const id = action.payload.rowId;
    const currentTableName: TableNameType = yield select(makeSelectorTableCurrentName());
    const tableName = action.payload.tableName ?? currentTableName;
    const url = MapTableNameToRemoveApi[tableName] + '/' + id;
    const selectRowIdCallback = MapTableNameToRemoveCallback[tableName];
    if (!url || !selectRowIdCallback) return;

    try {
        yield put(setTableIsLoading({ isLoading: true }));
        yield call(requestSaga, API_HOSTNAME + url, 'DELETE')
        yield put(tableRemoveRowSuccess({
            removeId: id,
            selectRowIdCallback,
            tableName,
        }))
    } catch (e: any) {
        yield put(tableRemoveRowFailure({ errorMessage: e }))
        yield call(checkResponseErrorWithModal, e);
    } finally {
        yield put(setTableIsLoading({ isLoading: false }));
    }
}

export function* tableModalOpenRemoveSaga(action: TTableModalOpenRemoveAction): SagaIterator {
    const currentTableName: TableNameType = yield select(makeSelectorTableCurrentName());
    const tableName = action.payload.tableName ?? currentTableName;
    const removeTranslationSelector = MapTableNameToRemoveRowTranslationSelector[tableName];
    if (!removeTranslationSelector) return;

    const removeMessage: string = (yield select(removeTranslationSelector())).modalRemoveMessage;

    yield put(modalOpen({
        render: (props: IModalRenderProps) => createElement(TableRemoveRowModalPopup, {
            message: removeMessage,
            onCloseModal: props.onCloseModal,
            rowId: action.payload.rowId,
        }),
    }))
}
